<style>
  .img {
    width: 42px;
  }

  /* ── Dark theme overrides for Materialize dropdown ── */
  :global(#dropdown1.dropdown-content) {
    background: #111820;
    border: 1px solid rgba(61, 220, 132, 0.12);
    border-radius: 6px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    min-width: 220px;
  }

  :global(#dropdown1.dropdown-content li > a.login-email) {
    font-size: 0.8rem;
    color: #9ca3af;
  }

  :global(#dropdown1.dropdown-content li > a) {
    color: #ccc8c2;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    padding: 0.6rem 1rem;
  }

  :global(#dropdown1.dropdown-content li:hover),
  :global(#dropdown1.dropdown-content li.active) {
    background-color: rgba(61, 220, 132, 0.08);
  }

  :global(#dropdown1.dropdown-content li > a:hover) {
    background: rgba(61, 220, 132, 0.08);
    color: #e8e6e3;
  }

  :global(#dropdown1.dropdown-content li > a > i) {
    color: rgba(61, 220, 132, 0.5);
  }

  :global(#dropdown1.dropdown-content li > a:hover > i) {
    color: #7ae8a4;
  }

  :global(#dropdown1.dropdown-content li.divider) {
    background-color: rgba(255, 255, 255, 0.06);
  }
</style>

<script>
  import { onMount } from "svelte";

  import { getAuth } from "./auth.js";
  import { clearGuestToken } from "./authSession.js";
  import { getUser } from "./api/user.js";
  import { user } from "./stores.js";
  import { layoutStore } from "./game/layout/LayoutStore.js";
  import { settingsStore } from "./game/SettingsStore.js";
  import { sshKeysOpen } from "./sshStore.js";

  export let isGuest = false;
  export let login = null; // For guest "Create Account" button

  const {
    isLoading,
    isAuthenticated,
    login: authLogin,
    logout,
    authToken,
    authError,
    userInfo,
  } = getAuth();

  async function loadUserData() {
    if (!$authToken) return;

    getUser(
      $authToken,
      (u) => {
        user.set(u);
      },
      (err) => console.error("Failed to load user data:", err)
    );
  }

  // Load user data whenever authToken changes and user is authenticated (skip for guests)
  $: if ($isAuthenticated && $authToken && !isGuest) {
    loadUserData();
  }

  onMount(() => {
    // Initialize Materialize dropdown
    const elems = document.querySelectorAll(".dropdown-trigger");
    if (elems.length > 0 && typeof M !== "undefined") {
      M.Dropdown.init(elems);
    }
  });

  function startLogin(connection) {
    const fn = login || authLogin;
    if (connection) fn(undefined, { connection });
    else fn();
  }
</script>

<!-- Dropdown Structure -->
<ul id="dropdown1" class="dropdown-content">
  {#if isGuest}
    <li>
      <!-- svelte-ignore a11y-invalid-attribute -->
      <a href="#!" on:click="{() => layoutStore.enterEditMode()}">
        <i class="material-icons" style="font-size: 1.2em; vertical-align: middle; margin-right: 0.5em;">dashboard_customize</i>
        Edit Layout
      </a>
    </li>
    <li>
      <!-- svelte-ignore a11y-invalid-attribute -->
      <a href="#!" on:click="{() => startLogin('twitter')}">
        <i class="material-icons" style="font-size: 1.2em; vertical-align: middle; margin-right: 0.5em;">login</i>
        Continue with X
      </a>
    </li>
    <li>
      <!-- svelte-ignore a11y-invalid-attribute -->
      <a href="#!" on:click="{() => startLogin('google-oauth2')}">
        <i class="material-icons" style="font-size: 1.2em; vertical-align: middle; margin-right: 0.5em;">login</i>
        Continue with Google
      </a>
    </li>
    <li>
      <!-- svelte-ignore a11y-invalid-attribute -->
      <a class="login-email" href="#!" on:click="{() => startLogin()}">
        <i class="material-icons" style="font-size: 1.2em; vertical-align: middle; margin-right: 0.5em;">mail</i>
        Email and password
      </a>
    </li>
    <li>
      <!-- svelte-ignore a11y-invalid-attribute -->
      <a href="#!" on:click="{() => settingsStore.openModal()}">
        <i class="material-icons" style="font-size: 1.2em; vertical-align: middle; margin-right: 0.5em;">settings</i>
        Settings
      </a>
    </li>
    <li class="divider"></li>
    <li>
      <!-- svelte-ignore a11y-invalid-attribute -->
      <a href="#!" on:click="{() => { clearGuestToken(); window.location.reload(); }}">
        <i class="material-icons" style="font-size: 1.2em; vertical-align: middle; margin-right: 0.5em;">logout</i>
        End Session
      </a>
    </li>
  {:else}
    <li>
      <!-- svelte-ignore a11y-invalid-attribute -->
      <a href="#!" on:click="{() => layoutStore.enterEditMode()}">
        <i class="material-icons" style="font-size: 1.2em; vertical-align: middle; margin-right: 0.5em;">dashboard_customize</i>
        Edit Layout
      </a>
    </li>
    <li>
      <!-- svelte-ignore a11y-invalid-attribute -->
      <a href="#!" on:click="{() => settingsStore.openModal()}">
        <i class="material-icons" style="font-size: 1.2em; vertical-align: middle; margin-right: 0.5em;">settings</i>
        Settings
      </a>
    </li>
    {#if $user && ($user.role === "creator" || $user.role === "admin")}
      <li>
        <a href="/creator" target="_blank">
          <i class="material-icons" style="font-size: 1.2em; vertical-align: middle; margin-right: 0.5em;">public</i>
          World Builder
        </a>
      </li>
    {/if}
    <li>
      <!-- svelte-ignore a11y-invalid-attribute -->
      <a href="#!" on:click="{() => sshKeysOpen.set(true)}">
        <i class="material-icons" style="font-size: 1.2em; vertical-align: middle; margin-right: 0.5em;">vpn_key</i>
        SSH keys
      </a>
    </li>
    <li class="divider"></li>
    <li>
      <!-- svelte-ignore a11y-invalid-attribute -->
      <a href="#!" on:click="{() => logout()}">
        <i class="material-icons" style="font-size: 1.2em; vertical-align: middle; margin-right: 0.5em;">logout</i>
        Log out
      </a>
    </li>
  {/if}
</ul>

{#if $isLoading}
  <li class="right-align">...</li>
{/if}
<li>

  <!-- svelte-ignore a11y-invalid-attribute -->
  <a class="dropdown-trigger" href="#" data-target="dropdown1">
    <span class="valign-wrapper">

      {#if $isAuthenticated}
        {#if isGuest}
          <i class="material-icons" style="font-size: 36px; color: #f59e0b;">person</i>
        {:else if $userInfo.picture}
          <img src="{$userInfo.picture}" alt="" class="circle img" />
        {:else}
          <i class="material-icons" style="font-size: 36px; color: #9ca3af;">person</i>
        {/if}
        <i class="material-icons left">arrow_drop_down</i>
      {/if}
    </span>

  </a>
</li>
